import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Building2, Mic2, ArrowRight } from "lucide-react";
import { http, fmtDate, fmtDateTime, fmtRupee, errMsg, downloadFile } from "../api";
import OrderDetailModal, { StageBadge } from "../components/OrderDetailModal";

const MODES = [
  ["projects", "Projects & payments"],
  ["activity", "Activity report"],
  ["summary", "Completion summary (margin)"],
  ["client-invoice", "Client invoice"],
  ["artist-payout", "Artist payout"],
];
const STATUSES = [["", "All stages"], ["PENDING_ARTIST_ACKNOWLEDGEMENT", "Awaiting ack"], ["IN_PROGRESS", "In progress"], ["DELIVERED", "Delivered"], ["REVISION_REQUESTED", "Revision"], ["COMPLETED", "Completed"], ["DECLINED", "Declined"]];

function toParams(range) {
  const p = {};
  if (range.start) p.start = new Date(range.start).toISOString();
  if (range.end) p.end = new Date(range.end + "T23:59:59").toISOString();
  return p;
}

function ProjectsReport({ data, onOpenOrder }) {
  const s = data.summary;
  return (
    <div className="card" data-testid="projects-report">
      <div className="stats">
        <div className="stat-card"><span>Projects in period</span><strong>{s.count}</strong><small>{s.completed_count} approved by client</small></div>
        <div className="stat-card"><span>Pipeline (active)</span><strong>{fmtRupee(s.pipeline_client_amount)}</strong><small>Not yet completed</small></div>
        <div className="stat-card success"><span>Client billed</span><strong>{fmtRupee(s.completed_client_billed)}</strong><small>Completed only</small></div>
        <div className="stat-card"><span>Artist payout</span><strong>{fmtRupee(s.completed_artist_payout)}</strong><small>Completed only</small></div>
        <div className="stat-card success"><span>Your commission</span><strong>{fmtRupee(s.completed_admin_commission)}</strong><small>Completed only</small></div>
      </div>
      <div className="chip-row" style={{ marginBottom: 12 }}>
        {Object.entries(s.by_stage).map(([k, v]) => <span key={k} className="badge">{k}: <strong>{v}</strong></span>)}
      </div>
      <div className="table-wrap">
        <table className="report-table" data-testid="projects-table">
          <thead><tr><th>Order</th><th>Client / Company</th><th>Artist</th><th>Project</th><th>Stage</th><th>Client ₹</th><th>Artist ₹</th><th>Commission ₹</th><th>Approved on</th></tr></thead>
          <tbody>
            {data.orders.map(o => (
              <tr key={o.id} onClick={() => onOpenOrder(o.id)} data-testid={`project-row-${o.id}`}>
                <td><strong>{o.order_code}</strong><br /><small>{fmtDate(o.created_at)}</small></td>
                <td><strong>{o.client_company || "Individual"}</strong><br /><small>{o.client_name}</small></td>
                <td>{o.artist_name}<br /><small>{o.artist_public_id}</small></td>
                <td>{o.project_title}<br /><small>{o.language} · {o.duration_hours}h {o.duration_is_final ? "final" : "estimated"}</small></td>
                <td><StageBadge status={o.status} /></td>
                <td>{fmtRupee(o.client_amount)}</td>
                <td>{fmtRupee(o.artist_payout)}</td>
                <td style={{ color: "var(--success)", fontWeight: 600 }}>{fmtRupee(o.admin_commission)}</td>
                <td>{o.completed_at ? fmtDate(o.completed_at) : "—"}</td>
              </tr>
            ))}
            {data.orders.length === 0 && <tr><td colSpan={9} style={{ textAlign: "center", color: "var(--muted)" }}>No projects in this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityReport({ data, onOpenOrder }) {
  return (
    <div className="card" data-testid="activity-report">
      <div className="card-head"><h2>{data.count} events</h2></div>
      <ul className="feed">
        {data.events.map((f, i) => (
          <li key={i} className="order" data-testid="activity-row">
            <span className="feed-dot" />
            <div>
              <div className="feed-text"><strong>{f.client_company || f.client_name}</strong> <ArrowRight size={11} style={{ display: "inline" }} /> <strong>{f.artist_name}</strong> — {f.event} <em>({f.project_title})</em></div>
              <small>{fmtDateTime(f.created_at)} · {f.actor} · <button className="link" onClick={() => onOpenOrder(f.order_id)}>{f.order_code}</button> · {fmtRupee(f.amount)}</small>
            </div>
          </li>
        ))}
        {data.events.length === 0 && <li style={{ color: "var(--muted)", fontSize: 13 }}>No activity in this range.</li>}
      </ul>
    </div>
  );
}

export default function AdminReports() {
  const [report, setReport] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [artists, setArtists] = useState([]);
  const [clients, setClients] = useState([]);
  const [mode, setMode] = useState("projects");
  const [selectedId, setSelectedId] = useState("");
  const [status, setStatus] = useState("");
  const [dateField, setDateField] = useState("created_at");
  const [filterClient, setFilterClient] = useState("");
  const [filterArtist, setFilterArtist] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    http.get("/admin/artists").then(r => setArtists(r.data));
    http.get("/admin/clients").then(r => setClients(r.data));
  }, []);

  const projectParams = () => ({ ...toParams(range), date_field: dateField, status, client_id: filterClient, artist_id: filterArtist });

  const run = async () => {
    setNotice(""); setReport(null); setBusy(true);
    try {
      let url = "/admin/reports/completed", params = toParams(range);
      if (mode === "projects") { url = "/admin/reports/projects"; params = projectParams(); }
      if (mode === "activity") url = "/admin/reports/activity";
      if (mode === "client-invoice") url = `/admin/reports/client-invoice/${selectedId}`;
      if (mode === "artist-payout") url = `/admin/reports/artist-payout/${selectedId}`;
      const { data } = await http.get(url, { params });
      setReport({ mode, data });
    } catch (e) { setNotice(errMsg(e)); } finally { setBusy(false); }
  };

  const excel = async () => {
    setNotice("");
    try {
      const params = mode === "projects" ? projectParams() : toParams(range);
      await downloadFile(`/admin/reports/${mode}`, { ...params, format: "xlsx" });
    } catch (e) { setNotice(errMsg(e)); }
  };

  const setPreset = days => {
    const end = new Date();
    const start = new Date(Date.now() - days * 86400000);
    setRange({ start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) });
  };
  const setThisMonth = () => {
    const d = new Date();
    setRange({ start: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10), end: d.toISOString().slice(0, 10) });
  };
  const print = () => window.print();

  return (
    <>
      <div className="page-head"><div><h1>Reports</h1><p>Daily, weekly, monthly or any custom range. Export to Excel in one click.</p></div></div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="chip-row" style={{ marginBottom: 12 }}>
          {MODES.map(([k, l]) => (
            <button key={k} className={`chip ${mode === k ? "active" : ""}`} onClick={() => { setMode(k); setReport(null); }} data-testid={`report-mode-${k}`}>{l}</button>
          ))}
        </div>
        {mode === "projects" && (
          <div className="filter-advanced">
            <select value={dateField} onChange={e => setDateField(e.target.value)} data-testid="projects-date-field"><option value="created_at">Filter by requested date</option><option value="completed_at">Filter by client approval date</option></select>
            <select value={status} onChange={e => setStatus(e.target.value)} data-testid="projects-status">{STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            <select value={filterClient} onChange={e => setFilterClient(e.target.value)} data-testid="projects-client"><option value="">All clients</option>{clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}</select>
            <select value={filterArtist} onChange={e => setFilterArtist(e.target.value)} data-testid="projects-artist"><option value="">All artists</option>{artists.map(a => <option key={a.id} value={a.id}>{a.name} ({a.public_id})</option>)}</select>
          </div>
        )}
        {mode === "client-invoice" && (
          <div style={{ marginBottom: 10 }}>
            <label>Client</label>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)}>
              <option value="">Select client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
            </select>
          </div>
        )}
        {mode === "artist-payout" && (
          <div style={{ marginBottom: 10 }}>
            <label>Artist</label>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)}>
              <option value="">Select artist</option>
              {artists.map(a => <option key={a.id} value={a.id}>{a.name} ({a.public_id})</option>)}
            </select>
          </div>
        )}
        <div className="field-row">
          <div><label>From</label><input type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} data-testid="report-start" /></div>
          <div><label>To</label><input type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} data-testid="report-end" /></div>
        </div>
        <div className="chip-row" style={{ marginTop: 8 }}>
          <button className="chip" onClick={() => setPreset(1)}>Today</button>
          <button className="chip" onClick={() => setPreset(7)}>Last 7 days</button>
          <button className="chip" onClick={() => setThisMonth()}>This month</button>
          <button className="chip" onClick={() => setPreset(30)}>Last 30 days</button>
          <button className="chip" onClick={() => setPreset(90)}>Last 90 days</button>
          <button className="chip" onClick={() => setRange({ start: "", end: "" })}>All time</button>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button className="primary" onClick={run} disabled={busy} data-testid="run-report">{busy ? "Generating…" : "Generate report"}</button>
          {(mode === "projects" || mode === "activity") && <button className="ghost" onClick={excel} data-testid="download-excel"><FileSpreadsheet size={14} /> Download Excel</button>}
        </div>
        {notice && <div className="notice error" style={{ marginTop: 10 }}>{notice}</div>}
      </div>

      {report?.mode === "projects" && <ProjectsReport data={report.data} onOpenOrder={setOpenId} />}
      {report?.mode === "activity" && <ActivityReport data={report.data} onOpenOrder={setOpenId} />}

      {report?.mode === "summary" && (
        <div className="card" data-testid="report-summary">
          <div className="card-head"><h2>Completion summary</h2><button className="ghost" onClick={print}><Download size={14} /> Print</button></div>
          <div className="stats">
            <div className="stat-card success"><span>Client billed</span><strong>{fmtRupee(report.data.total_client_billed)}</strong></div>
            <div className="stat-card"><span>Artist payout</span><strong>{fmtRupee(report.data.total_artist_payout)}</strong></div>
            <div className="stat-card success"><span>Admin margin</span><strong>{fmtRupee(report.data.admin_margin)}</strong></div>
            <div className="stat-card"><span>Completed orders</span><strong>{report.data.count}</strong></div>
          </div>
          <div className="list-rows">
            {report.data.orders.map(o => (
              <div key={o.id} className="list-row">
                <div className="list-row-info">
                  <strong>{o.order_code}</strong><p>{o.project_title} · {o.language} · {o.client_name} → {o.artist_name}</p>
                  <small style={{ color: "var(--muted)" }}>Completed {fmtDate(o.completed_at)}</small>
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 12.5 }}>
                  <div><div style={{ color: "var(--muted)" }}>Client</div><strong>{fmtRupee(o.final_amount ?? o.estimated_amount)}</strong></div>
                  <div><div style={{ color: "var(--muted)" }}>Artist</div><strong>{fmtRupee(o.artist_payout)}</strong></div>
                  <div><div style={{ color: "var(--muted)" }}>Margin</div><strong style={{ color: "var(--success)" }}>{fmtRupee((o.final_amount ?? o.estimated_amount) - o.artist_payout)}</strong></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {report?.mode === "client-invoice" && (
        <div className="card" data-testid="client-invoice">
          <div className="card-head"><h2>Invoice</h2><button className="ghost" onClick={print}><Download size={14} /> Print</button></div>
          <div className="detail-grid">
            <div>
              <div className="section-title">BILL TO</div>
              <strong>{report.data.client?.name}</strong>
              <p>{report.data.client?.company}</p><p>{report.data.client?.email}</p><p>{report.data.client?.phone}</p>
            </div>
            <div>
              <div className="kv"><span>Invoice #</span><span>{report.data.invoice_number}</span></div>
              <div className="kv"><span>Period</span><span>{fmtDate(report.data.start)} – {fmtDate(report.data.end)}</span></div>
            </div>
          </div>
          <div className="section-title">LINE ITEMS</div>
          {report.data.orders.map(o => (
            <div key={o.order_code} className="list-row">
              <div className="list-row-info"><strong>{o.order_code}</strong><p>{o.project_title} · {o.language} · {o.category}</p></div>
              <div style={{ display: "flex", gap: 12, fontSize: 12.5 }}><div>{o.hours}h × {fmtRupee(o.rate)}</div><strong>{fmtRupee(o.amount)}</strong></div>
            </div>
          ))}
          <div style={{ padding: 12, background: "var(--surface-2)", borderRadius: 10, marginTop: 12 }}>
            <div className="kv"><span>Subtotal</span><span>{fmtRupee(report.data.subtotal)}</span></div>
            <div className="kv"><span>GST (18%)</span><span>{fmtRupee(report.data.gst)}</span></div>
            <div className="kv"><span style={{ fontWeight: 700 }}>Total payable</span><span style={{ fontWeight: 700, fontSize: 16 }}>{fmtRupee(report.data.total)}</span></div>
          </div>
        </div>
      )}

      {report?.mode === "artist-payout" && (
        <div className="card" data-testid="artist-payout">
          <div className="card-head"><h2>Artist payout — ADMIN ONLY</h2><button className="ghost" onClick={print}><Download size={14} /> Print</button></div>
          <div className="detail-grid">
            <div>
              <div className="section-title">ARTIST</div>
              <strong>{report.data.artist?.name} ({report.data.artist?.public_id})</strong>
              <p>Base rate: {fmtRupee(report.data.artist?.base_rate)} / hour</p>
            </div>
            <div>
              <div className="section-title">PAYMENT DETAILS</div>
              {report.data.payment_details ? (
                <>
                  <div className="kv"><span>Account holder</span><span>{report.data.payment_details.account_holder}</span></div>
                  <div className="kv"><span>Bank</span><span>{report.data.payment_details.bank_name}</span></div>
                  <div className="kv"><span>A/c no.</span><span>{report.data.payment_details.account_number}</span></div>
                  <div className="kv"><span>IFSC</span><span>{report.data.payment_details.ifsc}</span></div>
                  {report.data.payment_details.upi_id && <div className="kv"><span>UPI</span><span>{report.data.payment_details.upi_id}</span></div>}
                </>
              ) : <p style={{ color: "var(--warning)" }}>Artist has not submitted payment details.</p>}
            </div>
          </div>
          <div className="section-title">COMPLETED ORDERS</div>
          {report.data.orders.map((o, i) => (
            <div key={i} className="list-row">
              <div className="list-row-info"><strong>{o.order_code}</strong><p>{o.project_title} · {o.language}</p><small style={{ color: "var(--muted)" }}>Completed {fmtDate(o.completed_at)}</small></div>
              <div style={{ display: "flex", gap: 12, fontSize: 12.5 }}><div>{o.hours}h × {fmtRupee(o.base_rate)}</div><strong>{fmtRupee(o.payout)}</strong></div>
            </div>
          ))}
          <div style={{ padding: 12, background: "var(--surface-2)", borderRadius: 10, marginTop: 12 }}>
            <div className="kv"><span style={{ fontWeight: 700 }}>Total payout</span><span style={{ fontWeight: 700, fontSize: 16, color: "var(--success)" }}>{fmtRupee(report.data.total_payout)}</span></div>
          </div>
        </div>
      )}
      {openId && <OrderDetailModal orderId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
